import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { catalogData, type CatalogItem } from '@/mocks/catalogData';

// Числа в дереве обязаны совпадать с блоком «База в цифрах»: и там, и тут —
// живые данные из /api/stats. Раньше дерево показывало захардкоженные цифры
// и расходилось с реальностью (напр. 15,5 млн против 18,7 млн по СОЮ).
const nfmt = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1).replace('.', ',')} млн`
    : n.toLocaleString('ru-RU').replace(/\u00a0/g, ' ');

const yrs = (r?: { min?: number | null; max?: number | null }) =>
  r && r.min && r.max ? ` · ${r.min}–${r.max}` : '';

const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
};

/** Живая подпись для узла дерева; null — оставить как в исходных данных. */
function liveLabel(id: string, st: any): string | null {
  if (!st) return null;
  const c = st.courts || {}, cy = st.court_years || {}, cats = st.categories || {};
  const dec = (n?: number, y?: any) =>
    n == null ? null : `${nfmt(n)} ${plural(n, 'решение', 'решения', 'решений')}${yrs(y)}`;
  switch (id) {
    case 'courts': return st.decisions_total ? `${nfmt(st.decisions_total)} решений` : null;
    case 'ks': return dec(c.ks, cy.ks);
    case 'vs': return dec(c.vs, cy.vs);
    case 'soyu': return dec(c.district, cy.district);
    case 'arb': return dec(c.arbitration, cy.arbitration);
    case 'fz': return cats.fz ? `${nfmt(cats.fz.docs)} ${plural(cats.fz.docs, 'закон', 'закона', 'законов')}${yrs(cats.fz.years)}` : null;
    case 'fkz': return cats.fkz ? `${nfmt(cats.fkz.docs)} ${plural(cats.fkz.docs, 'закон', 'закона', 'законов')}${yrs(cats.fkz.years)}` : null;
    case 'ukaz': return cats.ukaz ? `${nfmt(cats.ukaz.docs)} ${plural(cats.ukaz.docs, 'указ', 'указа', 'указов')}${yrs(cats.ukaz.years)}` : null;
    case 'pp': return cats.pp ? `${nfmt(cats.pp.docs)} ${plural(cats.pp.docs, 'постановление', 'постановления', 'постановлений')}${yrs(cats.pp.years)}` : null;
    case 'minfin': return cats.minfin ? `${nfmt(cats.minfin.docs)} ${plural(cats.minfin.docs, 'письмо', 'письма', 'писем')}` : null;
    case 'intl': return cats.intl ? `${nfmt(cats.intl.docs)} ${plural(cats.intl.docs, 'договор', 'договора', 'договоров')}` : null;
    case 'codexes': return cats.cd10200 ? `${nfmt(cats.cd10200.docs)} ${plural(cats.cd10200.docs, 'кодекс', 'кодекса', 'кодексов')} · ${nfmt(cats.cd10200.editions)} ред.` : null;
    default: return null;
  }
}

interface TreeNodeProps {
  item: CatalogItem;
  level: number;
  defaultOpen?: boolean;
  stats?: any;
}

function TreeNode({ item, level, defaultOpen = false, stats }: TreeNodeProps) {
  const live = liveLabel(item.id, stats);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const isFolder = item.type === 'folder';
  const hasChildren = item.children && item.children.length > 0;
  const isAllLink = item.id.endsWith('-all');

  // Indentation based on level
  const indentClass = level === 0 ? '' : level === 1 ? 'ml-3 md:ml-4' : level === 2 ? 'ml-3 md:ml-4' : 'ml-3 md:ml-4';

  // Styles based on level
  const getHeaderStyles = () => {
    if (level === 0) {
      return 'text-base md:text-lg font-bold text-foreground-950 py-2.5';
    }
    if (level === 1) {
      return 'text-sm md:text-base font-semibold text-foreground-900 py-2';
    }
    if (level === 2) {
      return 'text-sm font-medium text-foreground-800 py-1.5';
    }
    return 'text-sm text-foreground-700 py-1.5';
  };

  if (!isFolder) {
    // Document node
    const docLink = mapLink(item);
    return (
      <div className={`${indentClass} ${level >= 3 ? 'border-l border-background-200/50 pl-3 md:pl-4' : ''}`}>
        <a
          href={docLink}
          className={`group flex items-start gap-2 ${getHeaderStyles()} hover:text-primary-600 transition-colors ${
            isAllLink ? 'text-accent-600 font-medium' : ''
          }`}
        >
          {isAllLink ? (
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-arrow-right-line text-accent-500 text-xs" />
            </div>
          ) : (
            <div className="w-2 h-2 rounded-full bg-background-300 group-hover:bg-primary-400 flex-shrink-0 mt-2 transition-colors" />
          )}
          <span className="leading-relaxed">{item.title}</span>
          {item.date && (
            <span className="text-xs text-foreground-500 flex-shrink-0 ml-auto whitespace-nowrap mt-0.5">
              {item.date}
            </span>
          )}
          {(live || item.meta) && !item.date && (
            <span className="text-xs text-foreground-500 flex-shrink-0 ml-auto whitespace-nowrap mt-0.5">
              {live || item.meta}
            </span>
          )}
        </a>
      </div>
    );
  }

  // Folder node
  return (
    <div className={level === 0 ? 'mb-4 md:mb-6' : ''}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-2 text-left group ${getHeaderStyles()} hover:text-primary-600 transition-colors ${
          level === 0
            ? 'px-3 md:px-4 bg-background-100/60 hover:bg-background-100 rounded-lg border border-background-200/40'
            : level === 1
            ? ''
            : ''
        }`}
        aria-expanded={isOpen}
      >
        {hasChildren && (
          <div
            className={`w-5 h-5 flex items-center justify-center flex-shrink-0 rounded hover:bg-background-200/60 transition-all ${
              isOpen ? 'rotate-0' : '-rotate-90'
            }`}
          >
            <i className="ri-arrow-down-s-line text-foreground-500 text-lg" />
          </div>
        )}
        {!hasChildren && level === 0 && (
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-folder-3-line text-primary-500" />
          </div>
        )}
        <span className="leading-snug">{item.title}</span>
        {item.meta && (
          <span className="text-xs text-foreground-500 hidden sm:inline ml-1">— {item.meta}</span>
        )}
        {(live || item.count) && (
          <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full ml-auto flex-shrink-0 whitespace-nowrap">
            {live || item.count}
          </span>
        )}
      </button>

      {isOpen && hasChildren && (
        <div
          className={`animate-tree-fade-in ${
            level === 0
              ? 'mt-1 ml-2 md:ml-3 space-y-0.5'
              : level === 1
              ? 'mt-0.5 ml-5 md:ml-6 border-l-2 border-background-200/50 pl-3 md:pl-4 space-y-0.5'
              : 'mt-0.5 space-y-0.5'
          }`}
        >
          {item.children.map((child) => (
            <TreeNode key={child.id} item={child} level={level + 1} defaultOpen={level < 1} stats={stats} />
          ))}
        </div>
      )}
    </div>
  );
}


function mapLink(item: CatalogItem): string {
  if (item.link) return item.link;
  const id = item.id;
  const m = id.match(/^(codex|fz|fkz|ukaz|pp)-(\d+)$/);
  if (m) return `/${m[1]}/${m[2]}`;
  if (id === 'constitution') return '/constitution';
  if (id.startsWith('ks-') || id.startsWith('vs-')) return '/courts/district';
  if (id.startsWith('intl-')) return '/intl';
  return `/search?q=${encodeURIComponent((item.title || '').slice(0, 60))}`;
}


export default function CatalogTree() {
  // Те же живые данные, что и в блоке «База в цифрах»
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    fetch('/api/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => { if (v && v.decisions_total) setStats(v); })
      .catch(() => {});
  }, []);

  return (
    <section id="catalog" className="w-full py-10 md:py-16">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="mb-8 md:mb-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-primary-600" />
              </div>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Каталог документов
            </h2>
          </div>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
            Полная иерархия законодательства РФ — от Конституции до судебной практики.
          </p>
          <p className="text-foreground-500 text-xs md:text-sm mt-2 max-w-2xl">
            Напротив каждого раздела — <span className="font-medium text-foreground-700">сколько документов</span> в базе и <span className="font-medium text-foreground-700">за какие годы</span> они собраны.
          </p>
        </div>

        {/* Tree */}
        <div className="space-y-2">
          {catalogData.map((item) => (
            <TreeNode key={item.id} item={item} level={0} defaultOpen={true} stats={stats} />
          ))}
        </div>
      </div>
    </section>
  );
}